export const BASIC_PLAN = "basic";
export const LITE_PLAN = "lite";
export const PRO_PLAN = "pro";
export const PREMIUM_PLAN = "premium";

export const CLASSROOM_SCOPE = [
  "https://www.googleapis.com/auth/classroom.courses.readonly",
  "https://www.googleapis.com/auth/classroom.coursework.students",
  "https://www.googleapis.com/auth/classroom.rosters.readonly",
  "https://www.googleapis.com/auth/documents",
];

export const LOCAL_STORAGE_FEATURE_KEY = "essaygrader-version";
export const LOCAL_STORAGE_MENU_HIDDEN_KEY = "essaygrader-menu-hidden";
export const CONVERSION_PRICE_KEY = "essaygrader-conversion-price";

// LSK local storage key
export const LSK_PAYMENT_PROCESSING_SESSION_ID =
  "payment_processing_session_id";
export const LSK_SUGGESTION_FORM_SELECTED_RUBRIC =
  "suggestion_form:selectedRubric";

export const errorTable = {
  pausedLimitsExceeded:
    "Your plan is paused and you currently only have access to basic features. Resume your subscription to regain all the benefits you know and love",
  planWordLimitsExceeded:
    "The essay you are trying to upload is longer than your plan allows.",
  planEssayLimitsExceeded: "You have reached your monthly essay limit.",
  planAdvancedRubricEssaysLimitExceeded:
    "You have reached your limits for advanced rubric grading this month. Upgrade for unlimited use of Advanced Rubrics.",
  missedPermissions:
    "You need to grant the necessary permissions to access Google's API.",
  badGoogleResponse:
    "We experienced an error processing your request. Please try again.",
  essayTextMissed: "The provided essay is empty or too short to grade",
};

export const passwordResetFormText = {
  successTitle: "Email sent",
  successMessage:
    "Verification code was sent to your email address. Please enter the code to continue.",
  problemResettingPassword:
    "There was a problem resetting your password. Please check your email and try again, or contact support.",
  invalidEmail: "Please enter a valid email address.",
};

export const errorCode = {
  emailNotFound: "EMAIL_NOT_FOUND",
};

export const PLAN_INFO = {
  basic: {
    name: "Basic",
    essayLimit: 25,
    essayWordLimit: 1000,
    summaryLimit: 25,
    monthlyPrice: "0",
    yearlyPrice: "0",
    yearlyTotalPrice: "0",
    description: `Our basic plan is meant for those who want to test out the product.`,
    features: [
      "Grade <b>25</b> essays/mo",
      "Summarize <b>25</b> essays/mo",
      "<b>1000</b> essay word count limit",
      "Feedback & grammar reports",
      "Simple rubrics",
      "FERPA compliance",
      "Google Drive, Dropbox & Box integration",
    ],
  },
  lite: {
    name: "Lite",
    essayLimit: 100,
    essayWordLimit: 2000,
    summaryLimit: 100,
    monthlyPrice: "8.99",
    yearlyPrice: "6.99",
    yearlyTotalPrice: "83.88",
    description: `Our lite plan is perfect for those who don't grade essays a ton.`,
    features: [
      "<b>Everything in Basic, plus:</b>",
      "Grade <b>100</b> essays/mo",
      "Summarize <b>100</b> essays/mo",
      "<b>2000</b> essay word count limit",
      "Google Classroom integration",
      "Canvas LMS Integration",
    ],
  },
  pro: {
    name: "Pro",
    essayLimit: 350,
    essayWordLimit: 3500,
    summaryLimit: 350,
    monthlyPrice: "19.99",
    yearlyPrice: "14.99",
    yearlyTotalPrice: "179.88",
    description:
      "Our pro plan is our most popular plan. It's for those who grade a lot of essays per month.",
    features: [
      "<b>Everything in Lite, plus:</b>",
      "Grade <b>350</b> essays/mo",
      "<b>Manual</b> AI detection for <b>350</b> essays/mo",
      "Summarize <b>350</b> essays/mo",
      "<b>3500</b> essay word count limit",
      "Advanced rubrics (higher grading accuracy)",
      "Bulk essay upload",
      "Shared rubric library",
      "Class performance dashboard",
      "Priority email support",
    ],
  },
  premium: {
    name: "Premium",
    essayLimit: 700,
    essayWordLimit: 7000,
    summaryLimit: 700,
    monthlyPrice: "49.99",
    yearlyPrice: "34.99",
    yearlyTotalPrice: "419.88",
    description:
      "Our premium plan is for those true professionals who want the full capability of our product.",
    features: [
      "<b>Everything in Pro, plus:</b>",
      "Grade <b>800</b> essays/mo",
      "<b>Automatic</b> AI detection for <b>800</b> essays/mo",
      "Summarize <b>800</b> essays/mo",
      "<b>8,000</b> word count limit",
      "Priority chat support",
    ],
  },
};

export const PLATFORMS = Object.freeze({
  ESSAYGRADER: "essaygrader",
  GOOGLE: "google",
  GOOGLE_CLASSROOM: "googleClassroom", // TODO: deprecate
  CANVAS: "canvas",
});

export const SUPPORTED_UPLOAD_SERVICES = ["googleDrive", "oneDrive", "webcam"];

export const ERROR_BREAKDOWN_TYPES = {
  spelling: {
    color: "#027BFF",
    background: "#E6F2FF",
    title: "Spelling",
  },
  grammar: {
    color: "#F28C27",
    background: "#FFEAEC",
    title: "Grammar",
  },
  sentenceStructure: {
    color: "#14A2B8",
    background: "#EBFAFC",
    title: "Sentence structure",
  },
  coherenceGaps: {
    color: "#328C47",
    background: "#E9FFEE",
    title: "Coherence gaps",
  },
  clarityIssues: {
    color: "#CE316C",
    background: "#FFEAEC",
    title: "Clarity issues",
  },
  logicalInconsistencies: {
    color: "#704BD4",
    background: "#F3E8FC",
    title: "Logical inconsistencies",
  },
};

export const PRICING_PLANS = {
  teachers: [
    {
      id: "lite",
      name: "lite plan",
      discount: 30,
      description: "For those who don't grade a ton of essays",
      monthlyPrice: 8.99,
      yearlyPrice: 83.88,
      button: {
        text: "Select Lite",
        icon: "arrow-right",
      },
      featuresHeading: null,
      features: [
        "Grade 100 submissions per month",
        "2,000 word limit per submission",
        "FERPA, COPPA, SOC 2 Compliance",
        "Comprehensive Grammar reports",
      ],
    },
    {
      id: "pro",
      name: "pro plan",
      discount: 30,
      description: "Educators grading a lot of essays per month",
      monthlyPrice: 19.99,
      yearlyPrice: 179.88,
      button: {
        text: "Select Pro",
        icon: "arrow-right",
      },
      featuresHeading: "Everything in Lite Plus:",
      features: [
        "Grade 350 submissions per month",
        "3,500 word limit per submission",
        "Manual AI-writing detection ",
        "Google Classroom integration",
        "Canvas LMS integration ",
        "Schoology LMS integration ",
        "Assignment, Student, and Class level analytics",
      ],
    },
    {
      id: "premium",
      name: "premium plan",
      discount: 30,
      description:
        "Busy educators needing full access to our extensive features",
      monthlyPrice: 49.99,
      button: {
        text: "Select Premium",
      },
      yearlyPrice: 419.92,
      featuresHeading: "Everything in Pro Plus:",
      features: [
        "Grade 800 submissions per month",
        "8,000 word limit per submission",
        "Automatic AI-writing and Plagiarism detection",
      ],
    },
    {
      id: "district",
      name: "district",
      discount: null,
      description: "For Schools & Districts",
      monthlyPrice: 0,
      yearlyPrice: 0,
      button: {
        text: "Contact us",
      },
      featuresHeading: "Everything in District:",
      features: [
        "Number of submissions customized based on contract ",
        "No word limits per submission ",
        "AI-writing and Plagiarism detection ",
        "Prior listed integrations plus integrations with Teams, Blackboard and D2l Brightspace",
        "School and District wide analytics dashboards ",
        "Will sign custom or state-specific Digital Privacy Agreements (DPAs)",
        "White-glove onboarding and Training",
        "Instant Chat Support.",
      ],
      priceNote: "Contact us for discounted pricing",
      buttonVariant: "primary",
      secondaryButton: "Request a quote",
    },
  ],
  institutions: [
    {
      id: "team",
      name: "TEAM",
      discount: "30% OFF",
      description: "5+ users",
      monthlyPrice: 12.99,
      yearlyPrice: 155.88,
      features: [
        "Choose either Pro or Premium license only",
        "Training and support",
        "Single P.O. for all licenses",
        "Priority video support",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
        "—",
      ],
      priceNote: "Price starting at:",
      buttonText: "Book a demo",
      buttonVariant: "primary",
      secondaryButton: "Request a quote",
    },
  ],
};

export const FEATURE_LABELS = [
  "Grade essays per month",
  "Summarize essays per month",
  "Essay word count limit",
  "Integrations",
  "Advanced rubrics",
  "AI detection",
  "Bulk essay upload",
  "Shared rubric library",
  "Class performance dashboard",
  "Priority email support",
  "Priority chat support",
];

export const COMMON_USED_EMOJI = [
  "🥇",
  "🥈",
  "🏀",
  "⭐",
  "🔥",
  "💥",
  "👍",
  "🍀",
  "😫",
  "👨‍💻",
  "✌️",
  "☀️",
  "🧨",
  "😎",
  "🧛‍♂️",
  "😱",
  "🤦‍♀️",
  "😒",
  "🙌",
  "🙂",
  "🤔",
  "👩‍🏫",
  "😢",
];

export const INTENSITIES = [
  {
    label: "Easy",
    icon: "triangle-fill",
    color: "#28A745",
    value: "light",
  },
  {
    label: "Normal",
    icon: "square-fill",
    color: "#FFC107",
    value: "moderate",
  },
  {
    label: "Strict",
    icon: "pentagon-fill",
    color: "#DC3545",
    value: "intense",
  },
];

export const GRADE_LEVELS = [
  {
    label: "Elementary",
    icon: "triangle-fill",
    color: "#308C4A",
    backgroundColor: "#fff",
    value: "elementary",
  },
  {
    label: "Middle School",
    icon: "square-fill",
    color: "#D84B6B",
    backgroundColor: "#fff",
    value: "middleschool",
  },
  {
    label: "High School",
    icon: "pentagon-fill",
    color: "#1E88E5",
    backgroundColor: "#fff",
    value: "highschool",
  },
  {
    label: "College",
    icon: "hexagon-fill",
    color: "#CE00DD",
    backgroundColor: "#6f42c133",
    value: "college",
  },
];

export const FEEDBACK_LENGTH = [
  {
    label: "Detailed",
    value: "long",
  },
  {
    label: "Medium",
    value: "normal",
  },
  {
    label: "Short",
    value: "concise",
  },
];

export const MAP_GRADE_LEVELS_TO_FEEDBACK_LENGTH_INDEX = {
  elementary: 2,
  middleschool: 1,
  highschool: 0,
  college: 0,
};

export const ACCOUNT_LINKS = [
  {
    name: "My Account",
    path: "/account",
    icon: "setting-edit-filter-gear",
  },
  {
    name: "Subscription",
    path: "/account?tab=subscription",
    icon: "users-group",
  },
  {
    name: "Integrations",
    path: "/account?tab=integrations",
    icon: "integrations",
  },
];

export const WORKSPACE_LABELS = {
  yearly: {
    level1: "Year",
    level1Plural: "Years",
    level2: "Class",
    level2Plural: "Classes",
    assignment: "Assignment",
    addLevel1: "Add New",
    addLevel2: "Add Class",
    addAssignment: "Add Assignment",
  },
  class: {
    level1: "Class",
    level1Plural: "Classes",
    level2: "Period",
    level2Plural: "Periods",
    assignment: "Assignment",
    addLevel1: "Add New",
    addLevel2: "Add Period",
    addAssignment: "Add Assignment",
  },
  subject: {
    level1: "Subject",
    level1Plural: "Subjects",
    level2: "Class",
    level2Plural: "Classes",
    assignment: "Assignment",
    addLevel1: "Add New",
    addLevel2: "Add Class",
    addAssignment: "Add Assignment",
  },
  own: {
    level1: "Folder",
    level1Plural: "Folders",
    level2: "Assignment",
    level2Plural: "Assignments",
    addLevel1: "Add New",
    addLevel2: "Add Assignment",
  },
};

export const WORKSPACE_TYPES = {
  yearly: {
    title: "Year based",
    description: "Group by academic year, then class and assignments.",
  },
  class: {
    title: "Period based",
    description: "Group by class, then period and assignments.",
  },
  subject: {
    title: "Subject based",
    description: "Group by subject, then class and assignments.",
  },
  own: {
    title: "Create Your Own",
    description: "Create your own structure to match your workflow.",
  },
};

export const DEFAULT_UI_STRUCTURES = {
  yearly: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          expanded: true,
          children: [
            {
              id: 3,
              name: "",
            },
          ],
        },
      ],
    },
  ],
  class: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          expanded: true,
          children: [
            {
              id: 3,
              name: "",
            },
          ],
        },
      ],
    },
  ],
  subject: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          expanded: true,
          children: [
            {
              id: 3,
              name: "",
            },
          ],
        },
      ],
    },
  ],
  own: [
    {
      id: 1,
      name: "",
      expanded: true,
      children: [
        {
          id: 2,
          name: "",
          canNotExpand: true,
        },
      ],
    },
  ],
};

export const WORKSPACE_PLACEHOLDERS = {
  yearly: {
    level1: new Date().getFullYear().toString(),
    level2: "Name your class",
    assignment: "Name your assignment",
  },
  class: {
    level1: "Name your class",
    level2: "Name your period",
    assignment: "Name your assignment",
  },
  subject: {
    level1: "Name your subject",
    level2: "Name your class",
    assignment: "Name your assignment",
  },
  own: {
    level1: "Name your class",
    level2: "Name your assignment",
    assignment: "Name your assignment",
  },
};

export const UPLOAD_TYPE_OPTIONS = [
  { value: "files", label: "Select files" },
  { value: "text", label: "Enter Text" },
];

// External URLs
export const EXTERNAL_URLS = {
  SCHOOL_TRIAL: "https://essaygrader.fillout.com/school-trial",
};
